import React from 'react';
import {
  Box,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import CompareArrowsRoundedIcon from '@mui/icons-material/CompareArrowsRounded';
import { DashboardSectionTitle } from './DashboardSectionTitle';
import { formatKilos, formatPercent } from '../../utils/flujoCalculations';

const MetricCell = ({ value, maxValue, suffix = 'kg', formatter = formatKilos }) => {
  const percent = maxValue > 0 ? Math.min((value * 100) / maxValue, 100) : 0;

  return (
    <Box sx={{ minWidth: 120 }}>
      <Typography sx={{ fontSize: 14, fontWeight: 800, textAlign: 'right' }}>
        {formatter(value)} {suffix}
      </Typography>
      <Box
        sx={{
          height: 6,
          mt: 0.75,
          bgcolor: '#e5e7eb',
          borderRadius: 1,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            width: `${percent}%`,
            height: '100%',
            bgcolor: 'primary.main',
          }}
        />
      </Box>
    </Box>
  );
};

export const WarehouseComparisonTable = ({ rows }) => {
  const maxValues = rows.reduce(
    (acc, row) => ({
      kilosVolcados: Math.max(acc.kilosVolcados, row.kilosVolcados),
      kilosClasificados: Math.max(acc.kilosClasificados, row.kilosClasificados),
      industria: Math.max(acc.industria, row.industria),
      podrido: Math.max(acc.podrido, row.podrido),
      bajoCalibre: Math.max(acc.bajoCalibre, row.bajoCalibre),
      rendimiento: Math.max(acc.rendimiento, row.rendimiento),
    }),
    {
      kilosVolcados: 0,
      kilosClasificados: 0,
      industria: 0,
      podrido: 0,
      bajoCalibre: 0,
      rendimiento: 0,
    }
  );

  return (
    <Paper
      elevation={0}
      sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}
    >
      <Stack
        direction="row"
        spacing={2}
        sx={{ p: 2, pb: 1, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}
      >
        <DashboardSectionTitle icon={<CompareArrowsRoundedIcon />}>
          Comparativa por almacen
        </DashboardSectionTitle>
        <Typography sx={{ color: 'text.secondary', fontSize: 13, fontWeight: 700 }}>
          Barras relativas al almacen con mayor valor
        </Typography>
      </Stack>

      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 980 }}>
          <TableHead>
            <TableRow sx={{ bgcolor: '#fbfbfc' }}>
              <TableCell>Almacen</TableCell>
              <TableCell align="right">Volcados</TableCell>
              <TableCell align="right">Clasificados</TableCell>
              <TableCell align="right">Industria</TableCell>
              <TableCell align="right">Podrido</TableCell>
              <TableCell align="right">Bajo calibre</TableCell>
              <TableCell align="right">Rendimiento</TableCell>
              <TableCell align="right">Variedades</TableCell>
              <TableCell align="right">Registros</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.idAlmacen}>
                <TableCell sx={{ py: 2, fontWeight: 900 }}>{row.almacen}</TableCell>
                <TableCell align="right">
                  <MetricCell value={row.kilosVolcados} maxValue={maxValues.kilosVolcados} />
                </TableCell>
                <TableCell align="right">
                  <MetricCell value={row.kilosClasificados} maxValue={maxValues.kilosClasificados} />
                </TableCell>
                <TableCell align="right">
                  <MetricCell value={row.industria} maxValue={maxValues.industria} />
                </TableCell>
                <TableCell align="right">
                  <MetricCell value={row.podrido} maxValue={maxValues.podrido} />
                </TableCell>
                <TableCell align="right">
                  <MetricCell value={row.bajoCalibre} maxValue={maxValues.bajoCalibre} />
                </TableCell>
                <TableCell align="right">
                  <MetricCell
                    value={row.rendimiento}
                    maxValue={maxValues.rendimiento}
                    suffix="%"
                    formatter={formatPercent}
                  />
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 800 }}>{row.variedades}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 800 }}>{row.registros}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};
